import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";
import ThemeSwitcher from "../components/ThemeSwitcher";

import { apiErrorMessage } from "../services/api";
import { login } from "../services/authService";
import { pendingSignupOf } from "../services/signupService";
import TwoFactorChallenge from "./TwoFactorChallenge";

export default function Login({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Held in memory only — a reload drops it and the user starts over.
  const [challengeToken, setChallengeToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.twoFactorRequired) {
        setChallengeToken(result.challengeToken);
        setPassword("");
        return;
      }

      onLogin?.(result.user);
    } catch (err) {
      // A self sign-up that has not entered its email code: no session yet,
      // open the code screen (a code was sent if none was live; Resend is there).
      const pending = pendingSignupOf(err);
      if (pending) {
        navigate("/verify-email", { state: pending });
        return;
      }
      setError(apiErrorMessage(err, t("login.failed")));
    } finally {
      setLoading(false);
    }
  };

  /** Back to the password form, optionally explaining why. */
  const resetChallenge = (message = "") => {
    setChallengeToken(null);
    setError(message);
  };

  if (challengeToken) {
    return (
      <TwoFactorChallenge
        challengeToken={challengeToken}
        email={email}
        onVerified={onLogin}
        onExpired={resetChallenge}
        onBack={() => resetChallenge()}
      />
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{
        background:
          "radial-gradient(1000px 420px at 12% -10%, rgb(104 231 178 / 0.18), transparent)," +
          "radial-gradient(900px 420px at 88% 110%, rgb(252 99 85 / 0.12), transparent)," +
          "var(--pl-bg)",
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-sm p-6">
        <div className="mb-4 flex justify-end gap-2">
          <ThemeSwitcher compact />
          <LanguageSwitcher compact />
        </div>

        {/* Vertical lockup: symbol above the wordmark. Auth only — never in
            dense UI. */}
        <img
          src={`${process.env.PUBLIC_URL}/img/puralance_mark_512.png`}
          alt="Puralance"
          width={128}
          height={114}
          className="mx-auto mb-2 h-auto w-32"
        />
        <div className="mb-6 text-center text-xl font-bold text-ink">Puralance</div>

        <h1 className="mb-1 text-center text-xl font-bold">{t("login.title")}</h1>
        <p className="mb-6 text-center text-sm text-ink-2">{t("login.subtitle")}</p>

        {error && (
          <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
            {error}
          </div>
        )}

        <label className="field-label" htmlFor="email">
          {t("login.email")}
        </label>
        <input
          id="email"
          type="email"
          className="field-input mb-4"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />

        <label className="field-label" htmlFor="password">
          {t("login.password")}
        </label>
        <input
          id="password"
          type="password"
          className="field-input mb-6"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />

        <button type="submit" disabled={loading} className="btn-primary w-full">
          {loading ? t("login.submitting") : t("login.submit")}
        </button>

        <p className="mt-5 text-center text-sm text-ink-2">
          {t("login.noAccount")}{" "}
          <Link to="/pricing" className="font-semibold text-accent-text hover:underline">
            {t("login.seePlans")}
          </Link>
        </p>
      </form>
    </div>
  );
}
